/**
 * `alwaysEnableSeatReservationsFor` — addresses that ride the VIP seat wave
 * regardless of what tier their ticket is.
 *
 * Shared deliberately. The gate the visitor SEES (useScheduleControls) and the
 * gate that DECIDES (reserveSeat) have to agree on what "on the list" means; two
 * copies of this matching would eventually disagree, and the symptom would be a
 * Save a Seat button that returns "seat reservations aren't available" when
 * clicked. That is exactly the bug this file exists to prevent — it shipped that
 * way on 2026-09-01, client-side only, before the server gate was noticed.
 *
 * Pure — no Vue APIs, no imports — so it is safe in shared/ and unit-testable.
 * (ref/computed in a shared file crashes the Worker on cold start.)
 */

/**
 * Builder serves this field as [{ email }], but it is hand-maintained in a CMS,
 * so bare strings and a comma-separated string are accepted too, and case and
 * whitespace are normalised. An address that silently fails to match looks
 * exactly like the feature being broken for that one person.
 */
export function normalizeSeatAllowlist(raw) {
    const list = Array.isArray(raw)
        ? raw
        : (typeof raw === 'string' ? raw.split(',') : []);
    return new Set(
        list
            .map((entry) => (typeof entry === 'string' ? entry : (entry?.email ?? entry?.value ?? '')))
            .map((entry) => String(entry).trim().toLowerCase())
            .filter(Boolean)
    );
}

/**
 * A visitor with no email — logged out, or a session missing one — never
 * matches. Same conservative direction the VIP check takes: an unknown viewer
 * is treated as general admission rather than granted early access.
 */
export function isSeatAllowlisted(email, allowlist) {
    if (!email || !allowlist || typeof allowlist.has !== 'function') return false;
    return allowlist.has(String(email).trim().toLowerCase());
}
